import React, { useState, useEffect, useCallback } from "react";
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import { nationalities, countries } from "../assets/nationalities";

const SearchCard = ({
  keyword,
  employer,
  employers,
  user,
  handleSearchInputChange,
  handleEmployerChange,
  handleSearchSubmit,
  handleClearSearch
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // Custom styles for better visual appeal
  const styles = {
    card: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "none",
      background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)"
    },
    inputGroup: {
      borderRadius: "8px",
      border: "1px solid #e3e6f0",
      overflow: "hidden",
      transition: "all 0.2s ease-in-out",
      position: "relative"
    },
    button: {
      borderRadius: "8px",
      fontWeight: "500",
      transition: "all 0.2s ease-in-out",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    },
    filterBox: {
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px solid #e3e6f0",
      padding: "12px",
      maxHeight: "140px",
      overflowY: "auto"
    },
    badge: {
      borderRadius: "20px",
      fontSize: "0.75rem",
      fontWeight: "500"
    },
    suggestions: {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: "#ffffff",
      border: "1px solid #e3e6f0",
      borderTop: "none",
      borderRadius: "0 0 8px 8px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      zIndex: 1000,
      maxHeight: "200px",
      overflowY: "auto"
    },
    suggestionItem: {
      padding: "8px 12px",
      cursor: "pointer",
      borderBottom: "1px solid #f1f3f4",
      transition: "background-color 0.15s ease"
    },
    searchInput: {
      fontSize: "0.95rem",
      fontWeight: "400",
      transition: "all 0.2s ease"
    },
    filterGroup: {
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      padding: "16px",
      border: "1px solid #e3e6f0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
    },
    checkboxContainer: {
      maxHeight: "140px",
      overflowY: "auto",
      padding: "8px",
      backgroundColor: "#f8f9fa",
      borderRadius: "6px",
      border: "1px solid #e3e6f0"
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      marginBottom: "4px",
      cursor: "pointer",
      padding: "4px 8px",
      borderRadius: "4px",
      transition: "background-color 0.15s ease"
    },
    checkbox: {
      borderRadius: "4px",
      marginRight: "8px"
    },
    filterInput: {
      borderRadius: "0",
      fontSize: "0.9rem"
    }
  };

  // Popular search suggestions based on common biodata attributes
  const popularSearches = [
    t("Filipino"), t("Indonesian"), t("Indian"), t("Female"), t("Male"), 
    t("Experienced"), t("Single"), t("Married"), t("Christian"), t("Muslim"),
    t("Housemaid"), t("Caregiver"), t("Driver"), t("Cook"), t("Nanny")
  ];

  // Generate search suggestions based on input
  const generateSuggestions = (input) => {
    if (!input || input.length < 2) return [];
    
    const inputLower = input.toLowerCase();
    const suggestions = [];
    
    // Add matching nationalities
    nationalities.forEach(nationality => {
      if (nationality.toLowerCase().includes(inputLower)) {
        suggestions.push({
          type: 'nationality',
          value: nationality,
          icon: 'flag',
          label: `${t('Nationality')}: ${nationality}`
        });
      }
    });
    
    // Add matching countries for experience
    countries.forEach(country => {
      if (country.toLowerCase().includes(inputLower)) {
        suggestions.push({
          type: 'experience',
          value: country,
          icon: 'globe',
          label: `${t('Experience in')}: ${country}`
        });
      }
    });
    
    // Add popular searches
    popularSearches.forEach(search => {
      if (search.toLowerCase().includes(inputLower)) {
        suggestions.push({
          type: 'popular',
          value: search,
          icon: 'trending-up',
          label: search
        });
      }
    });
    
    return suggestions.slice(0, 8); // Limit to 8 suggestions
  };

  // Save search to local storage for recent searches
  const saveRecentSearch = (searchTerm) => {
    if (!searchTerm.trim()) return;
    
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const filtered = recent.filter(item => item !== searchTerm);
    const updated = [searchTerm, ...filtered].slice(0, 5);
    
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    setRecentSearches(updated);
  };

  // Load recent searches on component mount
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(saved);
  }, []);

  // Debounced search function for better performance
  const debouncedSearch = useCallback((searchTerm) => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 2) {
        const suggestions = generateSuggestions(searchTerm);
        setSearchSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, []);

  // Enhanced search input handler with suggestions
  const handleSearchInputChangeEnhanced = (e) => {
    const value = e.target.value;
    handleSearchInputChange(e);
    
    // Use debounced search for suggestions
    debouncedSearch(value);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    const event = { target: { value: suggestion.value } };
    handleSearchInputChange(event);
    saveRecentSearch(suggestion.value);
    setShowSuggestions(false);
  };

  const [advancedFilters, setAdvancedFilters] = useState({
    age_from: "18",
    age_to: "",
    marital_status: [],
    religion: [],
    nationality: [],
    experience: [],
    status: []
  });

  // Parse URL parameters on component mount and sync with basic search
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    
    // Sync advanced filters
    setAdvancedFilters({
      age_from: searchParams.get('age_from') || "18",
      age_to: searchParams.get('age_to') || "",
      marital_status: searchParams.get('marital_status') ? searchParams.get('marital_status').split(',').filter(Boolean) : [],
      religion: searchParams.get('religion') ? searchParams.get('religion').split(',').filter(Boolean) : [],
      nationality: searchParams.get('nationality') ? searchParams.get('nationality').split(',').filter(Boolean) : [],
      experience: searchParams.get('experience') ? searchParams.get('experience').split(',').filter(Boolean) : [],
      status: searchParams.get('status') ? searchParams.get('status').split(',').filter(Boolean) : []
    });

    // If there are advanced filters in URL, show the advanced filters section
    const hasAdvancedFiltersInUrl = searchParams.get('age_from') || searchParams.get('age_to') || 
                                   searchParams.get('marital_status') || searchParams.get('religion') ||
                                   searchParams.get('nationality') || searchParams.get('experience') ||
                                   searchParams.get('status');
    if (hasAdvancedFiltersInUrl) {
      setShowAdvancedFilters(true);
    }
  }, [location.search]);

  const handleAdvancedFilterChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setAdvancedFilters((prevFilters) => {
        const prevValues = prevFilters[name] || [];
        if (checked) {
          return {
            ...prevFilters,
            [name]: [...prevValues, value],
          };
        } else {
          return {
            ...prevFilters,
            [name]: prevValues.filter((item) => item !== value),
          };
        }
      });
    } else {
      setAdvancedFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    }
  };

  const handleAdvancedSubmit = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();
    
    // Add basic search params
    if (keyword.trim()) params.append('search', keyword.trim());
    if (employer) params.append('employer', employer);

    // Add advanced filter params
    params.append('age_from', advancedFilters.age_from || "18");
    params.append('age_to', advancedFilters.age_to || "");
    params.append('religion', advancedFilters.religion.join(','));
    params.append('marital_status', advancedFilters.marital_status.join(','));
    params.append('nationality', advancedFilters.nationality.join(','));
    params.append('experience', advancedFilters.experience.join(','));
    params.append('status', advancedFilters.status.join(','));

    // Merge with existing URL params
    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      currentParams.set(key, value);
    }

    history.push({
      pathname: window.location.pathname,
      search: currentParams.toString(),
    });
  };

  const handleClearAllFilters = () => {
    setAdvancedFilters({
      age_from: "18",
      age_to: "",
      marital_status: [],
      religion: [],
      nationality: [],
      experience: [],
      status: []
    });
    handleClearSearch();
    
    // Clear URL parameters
    history.push({
      pathname: window.location.pathname,
      search: "",
    });
  };

  const hasActiveFilters = () => {
    return keyword || employer || 
           advancedFilters.age_from !== "18" || 
           advancedFilters.age_to ||
           advancedFilters.marital_status.length > 0 ||
           advancedFilters.religion.length > 0 ||
           advancedFilters.nationality.length > 0 ||
           advancedFilters.experience.length > 0 ||
           advancedFilters.status.length > 0;
  };

  const handleBasicSubmit = (e) => {
    e.preventDefault();
    
    // If advanced filters are visible and have values, use the advanced submit
    if (showAdvancedFilters && (hasActiveFilters() || advancedFilters.age_from !== "18" || advancedFilters.age_to)) {
      handleAdvancedSubmit(e);
    } else {
      // Use the original search submit for basic search only
      handleSearchSubmit(e);
    }
  };

  return (
    <div className="card shadow-sm border-0" style={styles.card}>
      <div className="card-body p-4">
        {/* Search Header with Stats */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <h5 className="card-title mb-0 d-flex align-items-center">
              <Icon.Search className="me-2 text-primary" size={20} />
              {t("Search & Filter")}
            </h5>
            {hasActiveFilters() && (
              <span className="badge bg-primary ms-2" style={styles.badge}>
                {(() => {
                  const count = (advancedFilters.age_from !== "18" ? 1 : 0) +
                               (advancedFilters.age_to ? 1 : 0) +
                               advancedFilters.marital_status.length +
                               advancedFilters.religion.length +
                               advancedFilters.nationality.length +
                               advancedFilters.experience.length +
                               advancedFilters.status.length +
                               (keyword ? 1 : 0) +
                               (employer ? 1 : 0);
                  return t("{{count}} Active", { count });
                })()}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {recentSearches.length > 0 && (
              <small className="text-muted d-flex align-items-center">
                <Icon.Clock size={12} className="me-1" />
                {t("{{count}} Recent", { count: recentSearches.length })}
              </small>
            )}
            <small className="text-muted d-flex align-items-center">
              <Icon.Database size={12} className="me-1" />
              {t("Smart Search")}
            </small>
          </div>
        </div>

        {/* Basic Search Form */}
        <form onSubmit={handleBasicSubmit} className="mb-3">
          <div className="row g-3 align-items-end">
            {/* Enhanced Search Input and Action Buttons Row */}
            <div className="col-12">
              <label className="form-label text-muted small mb-2 fw-semibold">{t("Search")}</label>
              <div className="row g-2 align-items-end">
                {/* Search Input */}
                <div className={user.type === "agency" ? "col-lg-6 col-md-6" : "col-lg-8 col-md-8"}>
                  <div className="position-relative">
                    <div className="input-group" style={{...styles.inputGroup, position: "relative"}}>
                      <span className="input-group-text bg-light border-0">
                        <Icon.Search size={16} className="text-muted" />
                      </span>
                      <input
                        type="text"
                        className="form-control border-0 ps-0"
                        name="search"
                        id="search"
                        placeholder={t("Search by name, code, nationality, religion, employers, and country experience")}
                        value={keyword}
                        onChange={handleSearchInputChangeEnhanced}
                        onFocus={() => keyword.length >= 2 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleBasicSubmit(e);
                            saveRecentSearch(keyword);
                          }
                          if (e.key === 'Escape') {
                            setShowSuggestions(false);
                          }
                        }}
                        style={{ 
                          boxShadow: "none",
                          fontSize: "0.95rem"
                        }}
                        autoComplete="off"
                        aria-label={t("Search biodata")}
                        aria-describedby="search-help"
                      />
                      {keyword && (
                        <button
                          type="button"
                          className="btn btn-sm position-absolute end-0 top-50 translate-middle-y me-2 p-1"
                          style={{
                            zIndex: 10,
                            border: "none",
                            background: "transparent"
                          }}
                          onClick={handleClearSearch}
                          title={t("Clear search")}
                        >
                          <Icon.X size={14} className="text-muted" />
                        </button>
                      )}
                    </div>

                    {/* Search Suggestions Dropdown */}
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div style={styles.suggestions}>
                        {searchSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            style={{
                              ...styles.suggestionItem,
                              ...(index === searchSuggestions.length - 1 ? { borderBottom: "none" } : {})
                            }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                          >
                            <div className="d-flex align-items-center">
                              {suggestion.icon === 'flag' && <Icon.Flag size={14} className="me-2 text-primary" />}
                              {suggestion.icon === 'globe' && <Icon.Globe size={14} className="me-2 text-success" />}
                              {suggestion.icon === 'trending-up' && <Icon.TrendingUp size={14} className="me-2 text-warning" />}
                              <span className="text-sm">{suggestion.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recent Searches */}
                    {showSuggestions && !keyword && recentSearches.length > 0 && (
                      <div style={styles.suggestions}>
                        <div style={{...styles.suggestionItem, fontWeight: "500", color: "#6c757d", cursor: "default"}}>
                          <Icon.Clock size={14} className="me-2" />
                          {t("Recent Searches")}
                        </div>
                        {recentSearches.map((search, index) => (
                          <div
                            key={index}
                            style={styles.suggestionItem}
                            onClick={() => handleSuggestionClick({ value: search })}
                            onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                          >
                            <div className="d-flex align-items-center">
                              <Icon.RotateCcw size={14} className="me-2 text-muted" />
                              <span className="text-sm">{search}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Employer Select for Agency */}
                {user.type === "agency" && (
                  <div className="col-lg-3 col-md-3">
                    <label className="form-label text-muted small mb-2 fw-semibold">{t("Employers")}</label>
                    <div className="input-group" style={styles.inputGroup}>
                      <span className="input-group-text bg-light border-0">
                        <Icon.Users size={16} className="text-muted" />
                      </span>
                      <select 
                        name="employers" 
                        className="form-select border-0" 
                        value={employer} 
                        onChange={handleEmployerChange}
                        style={{ 
                          boxShadow: "none",
                          fontSize: "0.95rem"
                        }}
                      >
                        <option value="">{t("All Employers")}</option>
                        {employers.map((employer) => (
                          <option key={employer.id} value={employer.id}>
                            {employer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className={user.type === "agency" ? "col-lg-3 col-md-3" : "col-lg-4 col-md-4"}>
                  <div className="d-flex gap-2 flex-wrap">
                    <button type="submit" className="btn btn-primary flex-grow-1" style={styles.button}>
                      <Icon.Search size={16} className="me-1" /> {t("Search")}
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${showAdvancedFilters ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      title={t("Advanced Filters")}
                      style={{ ...styles.button, minWidth: "48px" }}
                    >
                      <Icon.Filter size={16} />
                    </button>
                    {hasActiveFilters() && (
                      <button 
                        type="button" 
                        className="btn btn-outline-danger" 
                        onClick={handleClearAllFilters} 
                        title={t("Clear all filters")}
                        style={{ ...styles.button, minWidth: "48px" }}
                      >
                        <Icon.X size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Search Help */}
              <div id="search-help" className="mt-2">
                <small className="text-muted d-flex align-items-center justify-content-between">
                  <span>
                    <Icon.HelpCircle size={12} className="me-1" />
                    {t("Press Enter to search, Esc to close suggestions")}
                  </span>
                  <span className="d-none d-md-inline">
                    {t("Type 2+ characters for smart suggestions")}
                  </span>
                </small>
              </div>
            </div>
          </div>
        </form>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="border-top pt-4 mt-3" style={{ borderColor: "#e3e6f0 !important" }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h6 className="mb-0 d-flex align-items-center text-secondary">
                <Icon.Sliders className="me-2" size={18} />
                {t("Advanced Filters")}
              </h6>
              <div className="d-flex gap-2">
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-info"
                  onClick={handleClearAllFilters}
                  style={styles.button}
                >
                  <Icon.RotateCcw size={14} className="me-1" />
                  {t("Reset")}
                </button>
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowAdvancedFilters(false)}
                  style={styles.button}
                >
                  <Icon.ChevronUp size={14} />
                </button>
              </div>
            </div>

            <form onSubmit={handleAdvancedSubmit}>
              <div className="row g-4">
                
                {/* Age Filter */}
                <div className="col-lg-2 col-md-6 col-sm-12">
                  <div style={styles.filterGroup}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Icon.Calendar className="me-2 text-primary" size={16} />
                      {t("Age Range")}
                    </label>
                    <div className="row g-2 d-flex flex-column">
                      <div className="col-6">
                        <label className="form-label small text-muted">{t("From")}</label>
                        <input 
                          type="number" 
                          name="age_from" 
                          className="form-control form-control-sm" 
                          onChange={handleAdvancedFilterChange} 
                          value={advancedFilters.age_from}
                          min="18"
                          max="65"
                          style={styles.filterInput}
                          placeholder="18"
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">{t("To")}</label>
                        <input 
                          type="number" 
                          name="age_to" 
                          className="form-control form-control-sm" 
                          onChange={handleAdvancedFilterChange} 
                          value={advancedFilters.age_to}
                          min="18"
                          max="65"
                          style={styles.filterInput}
                          placeholder="65"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Marital Status */}
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div style={styles.filterGroup}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Icon.Heart className="me-2 text-primary" size={16} />
                      {t("Marital Status")}
                    </label>
                    <div style={styles.checkboxContainer}>
                      {["Single", "Married", "Divorced", "Widowed", "Separated"].map((status) => (
                        <label key={status} style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="marital_status"
                            value={status}
                            onChange={handleAdvancedFilterChange}
                            checked={advancedFilters.marital_status.includes(status)}
                            style={styles.checkbox}
                          />
                          <span className="text-sm">{t(status)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Religion */}
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div style={styles.filterGroup}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Icon.Sun className="me-2 text-primary" size={16} />
                      {t("Religion")}
                    </label>
                    <div style={styles.checkboxContainer}>
                      {["Christian", "Catholic", "Muslim", "Hindu", "Buddhist", "Sikh", "Other"].map((religion) => (
                        <label key={religion} style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="religion"
                            value={religion}
                            onChange={handleAdvancedFilterChange}
                            checked={advancedFilters.religion.includes(religion)}
                            style={styles.checkbox}
                          />
                          <span className="text-sm">{t(religion)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div style={styles.filterGroup}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Icon.Activity className="me-2 text-primary" size={16} />
                      {t("Status")}
                    </label>
                    <div style={styles.checkboxContainer}>
                      {["Published", "Unavailable", "Removed", "Draft", "Booked", "On-Hold"].map((status) => (
                        <label key={status} style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            value={status}
                            onChange={handleAdvancedFilterChange}
                            checked={advancedFilters.status && advancedFilters.status.includes(status)}
                            style={styles.checkbox}
                          />
                          <span className="text-sm">{t(status)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Nationality */}
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div style={styles.filterGroup}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Icon.Flag className="me-2 text-primary" size={16} />
                      {t("Nationality")}
                    </label>
                    <div style={styles.checkboxContainer}>
                      {nationalities.slice(0, 10).map((nationality) => (
                        <label key={nationality} style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="nationality"
                            value={nationality}
                            onChange={handleAdvancedFilterChange}
                            checked={advancedFilters.nationality.includes(nationality)}
                            style={styles.checkbox}
                          />
                          <span className="text-sm">{t(nationality)}</span>
                        </label>
                      ))}
                      {nationalities.length > 10 && (
                        <small className="text-muted fst-italic">
                          {t("+ {{count}} more nationalities", { count: nationalities.length - 10 })}
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                {/* Experience Countries */}
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div style={styles.filterGroup}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Icon.Globe className="me-2 text-primary" size={16} />
                      {t("Experience Countries")}
                    </label>
                    <div style={{ ...styles.checkboxContainer, maxHeight: "120px" }}>
                      <div className="row g-1">
                        {countries.slice(0, 12).map((country) => (
                          <div className="col-lg-6 col-md-6 col-sm-6 col-6" key={country}>
                            <label style={{...styles.checkboxLabel, marginBottom: "2px"}}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                name="experience"
                                value={country}
                                onChange={handleAdvancedFilterChange}
                                checked={advancedFilters.experience.includes(country)}
                                style={styles.checkbox}
                              />
                              <span className="text-sm">{country}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                      {countries.length > 12 && (
                        <div className="text-center pt-2">
                          <small className="text-muted fst-italic">
                            {t("+ {{count}} more countries", { count: countries.length - 12 })}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-3 justify-content-between align-items-center pt-3 border-top" style={{ borderColor: "#e3e6f0 !important" }}>
                    <div className="d-flex align-items-center">
                      <Icon.Info size={16} className="text-muted me-2" />
                      <small className="text-muted">
                        {hasActiveFilters() ? (
                          t("{{count}} filters active", { 
                            count: (advancedFilters.age_from !== "18" ? 1 : 0) +
                                   (advancedFilters.age_to ? 1 : 0) +
                                   advancedFilters.marital_status.length +
                                   advancedFilters.religion.length +
                                   advancedFilters.nationality.length +
                                   advancedFilters.experience.length +
                                   advancedFilters.status.length
                          })
                        ) : (
                          t("No advanced filters applied")
                        )}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={handleClearAllFilters}
                        style={styles.button}
                      >
                        <Icon.RotateCcw size={16} className="me-1" /> {t("Clear All")}
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        style={styles.button}
                      >
                        <Icon.Search size={16} className="me-1" /> {t("Apply Filters")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

SearchCard.propTypes = {
  keyword: PropTypes.string.isRequired,
  employer: PropTypes.string.isRequired,
  employers: PropTypes.array.isRequired,
  user: PropTypes.shape({
    type: PropTypes.string.isRequired
  }).isRequired,
  handleSearchInputChange: PropTypes.func.isRequired,
  handleEmployerChange: PropTypes.func.isRequired,
  handleSearchSubmit: PropTypes.func.isRequired,
  handleClearSearch: PropTypes.func.isRequired
};

export default SearchCard;
